import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Notes from "./header";
import FooterManual from "./footerManual";
import { FaEye, FaCheckCircle, FaTimes } from "react-icons/fa";
import { TextField, Alert, Snackbar, Slide } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactFlagsSelect from "react-flags-select";

// Professional Success Message Component
const SuccessMessage = ({ open, onClose, message }) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={5000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' }}
    >
      <Alert
        onClose={onClose}
        severity="success"
        variant="filled"
        icon={<FaCheckCircle />}
        sx={{
          width: '100%',
          fontSize: '16px',
          fontWeight: '600',
          backgroundColor: '#4caf50',
          color: 'white',
          '& .MuiAlert-icon': { fontSize: '24px' },
          '& .MuiAlert-action': { color: 'white' }
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// Professional Error Message Component
const ErrorMessage = ({ open, onClose, message }) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' }}
    >
      <Alert
        onClose={onClose}
        severity="error"
        variant="filled"
        icon={<FaTimes />}
        sx={{
          width: '100%',
          fontSize: '16px',
          fontWeight: '600',
          backgroundColor: '#f44336',
          color: 'white',
          '& .MuiAlert-icon': { fontSize: '24px' },
          '& .MuiAlert-action': { color: 'white' }
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

// Reusable field label with red star
const FieldLabel = ({ text, htmlFor }) => (
  <label
    htmlFor={htmlFor}
    style={{
      fontSize: "16px",
      display: "block",
      marginBottom: "4px",
      marginTop: "10px",
      fontWeight: "600",
      color: "#333",
    }}
  >
    <span style={{ color: "red", marginRight: "3px" }}>*</span>
    {text}
  </label>
);

const SignUp = () => {
  const [selected, setSelected] = useState("");
  const [formData, setFormData] = useState({
    mobileNo: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    gender: "",
  });

  const [customLabels, setCustomLabels] = useState({});
  const [countryIdMap, setCountryIdMap] = useState({});

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(
          "https://car.amar.co.jp/api/authorization/shipping/all-countries",
          { headers: { "Accept-Language": "en" } }
        );
        const data = res.data;

        const labels = {};
        const idMap = {};
        data.forEach((c) => {
          labels[c.isoCode] = `${c.countryName} (${c.countryPhoneCode})`;
          idMap[c.isoCode] = c.countryId;
        });
        setCustomLabels(labels);
        setCountryIdMap(idMap);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!selected) {
      setNotificationMessage("Please select a country code to proceed.");
      setShowErrorMessage(true);
      setLoading(false);
      return;
    }

    if (!formData.gender) {
      setNotificationMessage("Please select your gender to proceed.");
      setShowErrorMessage(true);
      setLoading(false);
      return;
    }

    const countryCodeMatch = customLabels[selected].match(/\(([+\d-]+)\)/);
    const numericCode = countryCodeMatch ? countryCodeMatch[1] : "";

    const apiPayload = {
      countryId: countryIdMap[selected],
      mobileNo: formData.mobileNo,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      gender: formData.gender,
    };

    try {
      await axios.post(
        "https://car.amar.co.jp/api/authorization/auth/create-account",
        apiPayload,
        {
          headers: {
            "Accept-Language": "en",
            "Content-Type": "application/json",
          },
        }
      );

      localStorage.setItem("userCountryCode", numericCode);
      localStorage.setItem("userCountryFlag", selected);

      setNotificationMessage("Account created successfully! Redirecting to login page...");
      setShowSuccessMessage(true);

      setTimeout(() => {
        navigate("/logIn");
      }, 2000);

    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to create an account. Please try again.";
      setError(errorMsg);
      setNotificationMessage(errorMsg);
      setShowErrorMessage(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessMessage = () => setShowSuccessMessage(false);
  const handleCloseErrorMessage = () => setShowErrorMessage(false);

  return (
    <Box
      sx={{
        p: 0,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundImage: 'url(/image/shutterstock.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1,
        }}
      />

      {/* Content wrapper */}
      <Box
        sx={{
          width: '100%',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <SuccessMessage open={showSuccessMessage} onClose={handleCloseSuccessMessage} message={notificationMessage} />
        <ErrorMessage open={showErrorMessage} onClose={handleCloseErrorMessage} message={notificationMessage} />

        <Box sx={{ width: '100%' }}><Notes /></Box>

        {/* Breadcrumb */}
        <Box
          sx={{
            width: '100%',
            color: 'white',
            pt: 4, pb: 4,
            px: { xs: 2, md: 8 },
            mb: 4,
            textAlign: 'left',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#1E1E1E',
              padding: '10px 15px',
              borderRadius: '8px',
              display: 'inline-block',
            }}
          >
            <Typography component="span" sx={{ fontSize: '15px', color: '#A9A9A9', fontWeight: '400', marginRight: '5px' }}>
              Home /
            </Typography>
            <Typography component="span" sx={{ fontSize: '15px', color: 'white', fontWeight: '600' }}>
              SignUp
            </Typography>
          </Box>
        </Box>

        {/* Form Container */}
        <Box
          sx={{
            width: { xs: '85%', sm: '70%', md: '40%', lg: '30%' },
            backgroundColor: "#FFFFFF",
            border: "1px solid #EBEBEB",
            borderRadius: "10px",
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            p: 3,
            mb: 8,
          }}
        >
          {/* Title */}
          <Box
            sx={{
              backgroundColor: "#fafafa",
              height: "50px",
              borderBottom: "1px solid #ebebeb",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              mb: 2,
              px: 2,
            }}
          >
            <Box component="img" src="/image/Login-icon-image.svg" sx={{ height: "25px" }} />
            <Typography sx={{ fontWeight: "700", fontSize: "18px", color: '#333' }}>
              CREATE ACCOUNT
            </Typography>
          </Box>

          {/* Form */}
          <Box sx={{ padding: "0 20px" }}>
            <form onSubmit={handleSubmit}>

              {/* Required fields note */}
              <Typography sx={{ fontSize: "12px", fontWeight: "600", color: "#555", mt: 1, mb: 0.5 }}>
                <span style={{ color: "red" }}>*</span> Required fields
              </Typography>

              {/* First Name */}
              <FieldLabel text="First Name" htmlFor="firstName" />
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "2px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: "500",
                  boxSizing: "border-box",
                }}
                placeholder="Enter your first name"
                required
              />

              {/* Last Name */}
              <FieldLabel text="Last Name" htmlFor="lastName" />
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "2px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: "500",
                  boxSizing: "border-box",
                }}
                placeholder="Enter your last name"
                required
              />

              {/* Gender */}
              <FieldLabel text="Gender" htmlFor="gender" />
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "2px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: "500",
                  color: formData.gender ? "#000" : "#777",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  appearance: "auto",
                  boxSizing: "border-box",
                }}
              >
                <option value="" disabled>Select your gender</option>
                <option style={{ color: "#000" }} value="Male">Male</option>
                <option style={{ color: "#000" }} value="Female">Female</option>
                <option style={{ color: "#000" }} value="Other">Other</option>
              </select>

              {/* Email */}
              <FieldLabel text="E-mail" htmlFor="email" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "2px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "16px",
                  fontWeight: "500",
                  boxSizing: "border-box",
                }}
                placeholder="Enter your email"
                required
              />

              {/* Mobile */}
              <FieldLabel text="Mobile Number" htmlFor="mobileNo" />
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  '& .custom-button': {
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: '500',
                    minWidth: '220px',
                  }
                }}
              >
                <ReactFlagsSelect
                  selected={selected}
                  onSelect={(code) => setSelected(code)}
                  customLabels={customLabels}
                  countries={Object.keys(customLabels)}
                  placeholder="Select Country"
                  searchable
                  searchPlaceholder="Search countries"
                  selectButtonClassName="custom-button"
                  style={{ flex: '0 0 auto', minWidth: '220px' }}
                />

                <TextField
                  type="text"
                  name="mobileNo"
                  value={formData.mobileNo}
                  required
                  variant="outlined"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '55px',
                      '& .MuiInputBase-input': {
                        padding: '10px',
                        height: '100%',
                        boxSizing: 'border-box',
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      marginTop: '2px',
                      marginLeft: '2px',
                    }
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (!/^\d*$/.test(value)) return;
                    if (value.length > 10) return;
                    handleChange(e);
                    if (selected) setError("");
                  }}
                  onBlur={() => {
                    if (selected && formData.mobileNo.length !== 10) {
                      setError("Your mobile number is incorrect. Please enter a 10-digit number.");
                    } else {
                      setError("");
                    }
                  }}
                  placeholder={
                    selected
                      ? `e.g. ${customLabels[selected].match(/\(([+\d-]+)\)/)?.[1] || '123'} 1234567890`
                      : "Select country first"
                  }
                  disabled={!selected}
                  error={Boolean(error)}
                  helperText={error}
                />
              </Box>

              {/* Password */}
              <FieldLabel text="Password" htmlFor="password" />
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={isHovered ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    paddingRight: "40px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "16px",
                    fontWeight: "500",
                    boxSizing: "border-box",
                  }}
                  placeholder="Enter your password"
                  required
                />
                <span
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#777",
                  }}
                >
                  <FaEye size={20} />
                </span>
              </div>

              <Stack direction="row">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#eb2329",
                    width: "100%",
                    marginTop: "20px",
                    padding: "12px 0",
                    fontSize: "16px",
                    fontWeight: 'bold',
                    ":hover": { backgroundColor: "#d02026" },
                  }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "CREATING ACCOUNT..." : "SIGNUP"}
                </Button>
              </Stack>
            </form>

            <Typography sx={{ fontSize: "15px", textAlign: 'center', marginTop: "15px", marginBottom: '10px' }}>
              Already have an account?
              <a
                href="https://car.amar.co.jp/logIn"
                style={{
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "bold",
                  color: "#eb2329",
                  marginLeft: "5px",
                  textDecoration: 'none'
                }}
              >
                Log in
              </a>
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ width: '100%', mt: 'auto', zIndex: 2 }}>
        <FooterManual />
      </Box>

      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        html, body, #root {
          height: 100%;
        }
      `}</style>
    </Box>
  );
};

export default SignUp;